import { useEffect, useMemo, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import mapboxpin from './assets/mapbox_pin.svg';
import mapboxpinLight from './assets/mapbox_pin_light.svg';
import mapboxPinRed from '../../../../../assets/images/marker-red.svg';
import mapboxPinBlue from '../../../../../assets/images/marker-blue.svg';
import mapboxPinGreen from '../../../../../assets/images/marker-green.svg';
import mapboxPinYellow from '../../../../../assets/images/marker-yellow.svg';
import mapboxPinPurple from '../../../../../assets/images/marker-purple.svg';
import mapboxPinPoi from '../../../../../assets/images/marker-poi.svg';
import config from '../../../../../config';

const { username, styleId, accessToken } = config.mapbox;
mapboxgl.accessToken = accessToken;

export function useMapboxMap(container, options) {
  const [instance, setInstance] = useState(null);

  useEffect(() => {
    if (!container.current) return;
    if (options.enabled === false) return;

    const map = new mapboxgl.Map({
      container: container.current,
      style: `mapbox://styles/${username}/${styleId}`,
      ...options,
    });

    const marker = new Image();
    marker.src = mapboxpin;

    const markerLight = new Image();
    markerLight.src = mapboxpinLight;

    const markerRed = new Image();
    markerRed.src = mapboxPinRed;

    const markerBlue = new Image();
    markerBlue.src = mapboxPinBlue;

    const markerGreen = new Image();
    markerGreen.src = mapboxPinGreen;

    const markerYellow = new Image();
    markerYellow.src = mapboxPinYellow;

    const markerPurple = new Image();
    markerPurple.src = mapboxPinPurple;

    const markerPoi = new Image();
    markerPoi.src = mapboxPinPoi;

    const loadMap = new Promise((resolve) => {
      map.on('load', resolve);
    });
    const loadMarker = new Promise((resolve) => {
      marker.addEventListener('load', resolve);
    });
    const loadMarkerLight = new Promise((resolve) => {
      markerLight.addEventListener('load', resolve);
    });
    const loadMarkerRed = new Promise((resolve) => {
      markerRed.addEventListener('load', resolve);
    });
    const loadMarkerBlue = new Promise((resolve) => {
      markerBlue.addEventListener('load', resolve);
    });
    const loadMarkerGreen = new Promise((resolve) => {
      markerGreen.addEventListener('load', resolve);
    });
    const loadMarkerYellow = new Promise((resolve) => {
      markerYellow.addEventListener('load', resolve);
    });
    const loadMarkerPurple = new Promise((resolve) => {
      markerPurple.addEventListener('load', resolve);
    });
    const loadMarkerPoi = new Promise((resolve) => {
      markerPoi.addEventListener('load', resolve);
    });

    Promise.all([
      loadMap,
      loadMarker,
      loadMarkerLight,
      loadMarkerRed,
      loadMarkerBlue,
      loadMarkerGreen,
      loadMarkerYellow,
      loadMarkerPurple,
      loadMarkerPoi,
    ]).then(() => {
      map.addImage('marker', marker);
      map.addImage('markerLight', markerLight);
      map.addImage('markerRed', markerRed);
      map.addImage('markerBlue', markerBlue);
      map.addImage('markerGreen', markerGreen);
      map.addImage('markerYellow', markerYellow);
      map.addImage('markerPurple', markerPurple);
      map.addImage('markerPoi', markerPoi);
      setInstance(map);
    });

    // eslint-disable-next-line consistent-return
    return () => {
      map.remove();
    };
  }, [container, options.enabled]);

  return instance;
}

export function useIsFiltered(filterOptions) {
  return useMemo(() => {
    const { deduplicate } = filterOptions;
    const deduplicated = deduplicate === '1';
    const otherFiltering =
      Object.keys(filterOptions).filter((option) => {
        return !['deduplicate'].includes(option) && filterOptions[option].length > 0;
      }).length > 0;

    return deduplicated || otherFiltering;
  }, [filterOptions]);
}
